<template>
  <div class="file-upload-container">
    <div
      class="file-drop-zone"
      :class="{
        'drag-over': isDragOver,
        'has-file': selectedFile,
        'has-error': hasError,
      }"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      @click="triggerFileInput"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        style="display: none"
        @change="onFileSelect"
      />

      <div class="upload-content">
        <div v-if="!selectedFile" class="upload-placeholder">
          <div class="upload-icon">
            <i class="fa fa-cloud-upload fa-3x"></i>
          </div>
          <p class="upload-text">Drag and drop a file here or click</p>
          <p class="upload-hint">{{ acceptedFormatsText }}</p>
        </div>

        <div v-else class="file-preview">
          <div class="file-info">
            <i class="fa fa-file-excel-o fa-2x text-success"></i>
            <div class="file-details">
              <p class="file-name">{{ selectedFile.name }}</p>
              <p class="file-size">{{ formatFileSize(selectedFile.size) }}</p>
            </div>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-danger remove-file"
            @click.stop="removeFile"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>

        <div v-if="hasError" class="error-message">
          <i class="fa fa-exclamation-triangle"></i>
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "FileUpload",

  props: {
    accept: {
      type: String,
      default: ".xlsx,.xls",
    },
    maxSize: {
      type: Number,
      default: 10 * 1024 * 1024, // 10MB
    },
  },

  data() {
    return {
      selectedFile: null,
      isDragOver: false,
      hasError: false,
      errorMessage: "",
    };
  },

  computed: {
    acceptedFormatsText() {
      if (this.accept.includes(".xlsx") || this.accept.includes(".xls")) {
        return "Excel files only (.xlsx, .xls)";
      }
      return `Accepted formats: ${this.accept}`;
    },
  },

  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    onDragOver(e) {
      this.isDragOver = true;
    },

    onDragLeave(e) {
      this.isDragOver = false;
    },

    onDrop(e) {
      this.isDragOver = false;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    },

    onFileSelect(e) {
      const file = e.target.files[0];
      if (file) {
        this.handleFile(file);
      }
    },

    handleFile(file) {
      this.hasError = false;
      this.errorMessage = "";

      // Validate file type
      if (!this.isValidFileType(file)) {
        this.showError(
          `The file is not allowed (${this.acceptedFormatsText.toLowerCase()}).`
        );
        return;
      }

      // Validate file size
      if (file.size > this.maxSize) {
        this.showError(
          `File size is too large. Maximum allowed size is ${this.formatFileSize(
            this.maxSize
          )}.`
        );
        return;
      }

      this.selectedFile = file;
      this.$emit("change", { target: { files: [file] } });
    },

    isValidFileType(file) {
      if (!this.accept) return true;

      const acceptedTypes = this.accept.split(",").map((type) => type.trim());
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();

      return acceptedTypes.some((type) => {
        if (type.startsWith(".")) {
          return fileExtension === type.toLowerCase();
        }
        return file.type === type;
      });
    },

    showError(message) {
      this.hasError = true;
      this.errorMessage = message;
      this.selectedFile = null;
      this.$emit("change", { target: { files: [] } });
    },

    removeFile() {
      this.selectedFile = null;
      this.hasError = false;
      this.errorMessage = "";
      this.$refs.fileInput.value = "";
      this.$emit("change", { target: { files: [] } });
      this.$emit("remove");
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";

      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
  },
};
</script>

<style scoped>
.file-upload-container {
  width: 100%;
}

.file-drop-zone {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #f9fafb;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-drop-zone:hover {
  border-color: #3b82f6;
  background-color: #eff6ff;
}

.file-drop-zone.drag-over {
  border-color: #3b82f6;
  background-color: #dbeafe;
}

.file-drop-zone.has-file {
  border-color: #10b981;
  background-color: #f0fdf4;
}

.file-drop-zone.has-error {
  border-color: #ef4444;
  background-color: #fef2f2;
}

.upload-content {
  width: 100%;
}

.upload-placeholder {
  color: #6b7280;
}

.upload-icon {
  margin-bottom: 1rem;
  color: #9ca3af;
}

.upload-text {
  font-size: 1rem;
  font-weight: 500;
  margin: 0.5rem 0;
  color: #374151;
}

.upload-hint {
  font-size: 0.875rem;
  margin: 0;
  color: #6b7280;
}

.file-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background-color: white;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.file-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.file-details {
  margin-left: 1rem;
  text-align: left;
}

.file-name {
  font-weight: 500;
  margin: 0 0 0.25rem 0;
  color: #111827;
}

.file-size {
  font-size: 0.875rem;
  margin: 0;
  color: #6b7280;
}

.remove-file {
  padding: 0.25rem 0.5rem;
  margin-left: 1rem;
}

.error-message {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  color: #dc2626;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
}

.error-message i {
  margin-right: 0.5rem;
}

.text-success {
  color: #10b981 !important;
}
</style>
